import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, CalendarClock, Zap, Trophy, Newspaper, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { TEAM_INFO, orderedTeamIdsForSeason } from "../../lib/teamMap";
import { promptPush, optOutPush, getPushState, osSyncTags } from "../../lib/onesignal";
import { DELAY_STEPS, DELAY_LABEL } from "../admin/NotificationsAdmin";

const TOPICS = [
    ["topic_schedule", "Schedule reminders", "15 min before + puck drop", CalendarClock],
    ["topic_goals", "Goals", "Every goal, as it happens", Zap],
    ["topic_final", "Final score", "The result when a game ends", Trophy],
    ["topic_news", "PWHL News", "Breaking PWHL news & signings", Newspaper],
];
// Per-team topic chips. News is PWHL-only.
export const CHIPS = [
    ["sched", "Schedule", CalendarClock, "both"],
    ["goals", "Goals", Zap, "both"],
    ["final", "Final", Trophy, "both"],
    ["news", "News", Newspaper, "pwhl"],
];

const Toggle = ({ on }) => (
    <span className={`relative inline-flex items-center w-10 h-5 rounded-full shrink-0 transition-colors ${on ? "bg-[#9D4CDD]" : "bg-white/15"}`}>
        <span className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? "left-[calc(100%-1.125rem)]" : "left-0.5"}`} />
    </span>
);

// Every OneSignal tag this account should carry, from the preference map.
export const isStandaloneApp = () => typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
const deviceLabel = () => { const u = navigator.userAgent; return /iPhone/i.test(u) ? "iPhone" : /iPad/i.test(u) ? "iPad" : /Android/i.test(u) ? "Android" : "phone"; };

export function tagsFrom(prefs) {
    const t = {};
    Object.keys(prefs).forEach((k) => {
        if (k.startsWith("topic_") || k.startsWith("league_") || k.startsWith("team_")) t[k] = !!prefs[k];
    });
    TOPICS.forEach(([k]) => { t[k] = true; }); // type masters removed — team/league only
    return t;
}

export default function NotificationPrefs({ initial, onSave }) {
    const { user } = useAuth();
    const [prefs, setPrefs] = useState(() => ({ ...(initial || {}) }));
    const [state, setState] = useState({ supported: true, optedIn: false });
    const [busy, setBusy] = useState(false);

    useEffect(() => { getPushState().then(setState); }, []);
    // Home-screen app registers itself on the account, so desktop can manage the PHONE's alerts.
    useEffect(() => {
        if (!state.optedIn || !isStandaloneApp() || prefs.push_enabled === false) return;
        const last = Date.parse(prefs.app_device?.at || 0) || 0;
        if (Date.now() - last > 6 * 3600 * 1000) persist({ ...prefs, app_device: { at: new Date().toISOString(), device: deviceLabel() } });
    }, [state.optedIn]); // eslint-disable-line react-hooks/exhaustive-deps

    const { data: nhlTeams = [] } = useQuery({
        queryKey: ["nhl-teams"], queryFn: () => api.get("/nhl/teams").then((r) => r.data),
        enabled: !!user, staleTime: 60 * 60 * 1000,
    });
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
    const globalDelay = Number(settings?.game_delay_sec || 0);
    const myDelay = prefs.game_delay_sec;   // undefined = use global
    const setDelay = (raw) => {
        const next = { ...prefs };
        if (raw === "") delete next.game_delay_sec; else next.game_delay_sec = Number(raw);
        persist(next);
    };
    const pwhlTeams = useMemo(
        () => orderedTeamIdsForSeason("2026-27").map((id) => ({ id, name: TEAM_INFO[id]?.name || id, color: TEAM_INFO[id]?.color || "#9D4CDD" })),
        []
    );

    const persist = (next) => { setPrefs(next); osSyncTags(tagsFrom(next)); onSave(next); };
    const setKey = (k, v) => persist({ ...prefs, [k]: v });

    const enable = async () => {
        setBusy(true);
        const ok = await promptPush();
        setBusy(false);
        setState(await getPushState());
        if (ok) { persist({ ...prefs, push_enabled: true }); toast.success("Push notifications enabled"); }
        else toast.error("Notifications were not enabled (check your browser's site permissions)");
    };
    const disable = async () => {
        optOutPush(); setState((s) => ({ ...s, optedIn: false }));
        const next = { ...prefs, push_enabled: false };
        if (isStandaloneApp()) delete next.app_device;
        persist(next); toast.success("Push turned off");
    };
    const appDevice = prefs.app_device && !isStandaloneApp() ? prefs.app_device : null;

    if (state.supported === false && !appDevice) {
        return <p className="text-sm text-white/60" data-testid="push-unsupported">Your browser doesn't support web push. Try Chrome, Edge, Firefox, or add Rosterly to your Home Screen on iPhone/iPad (iOS 16.4+).</p>;
    }
    const enabled = (state.optedIn || !!appDevice) && prefs.push_enabled !== false;

    return (
        <div className="space-y-6" data-testid="notif-prefs">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <span className="flex items-center gap-3">
                    {enabled ? <Bell className="w-5 h-5 text-[#9D4CDD]" /> : <BellOff className="w-5 h-5 text-white/40" />}
                    <span><span className="block text-sm font-semibold text-white/90">Push notifications</span>
                        <span className="block text-[11px] text-white/45">{appDevice && !state.optedIn ? "Changes here update the alerts on your home-screen app" : enabled ? "On for this device" : "Turn on to get alerts here"}</span></span>
                </span>
                {appDevice && !state.optedIn
                    ? <span className="text-[10px] font-bold uppercase tracking-wider text-[#22C55E]" data-testid="push-app-linked">Managing your {appDevice.device || "phone"} app</span>
                    : enabled
                    ? <button onClick={disable} data-testid="push-disable" className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-white/10 text-white/70 hover:text-white">Turn off</button>
                    : <button onClick={enable} disabled={busy} data-testid="push-enable" className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-[#9D4CDD] hover:bg-[#B366F0] text-white disabled:opacity-50">{busy ? "…" : "Enable"}</button>}
            </div>

            <div className="mb-6" />

            <div className={enabled ? "" : "opacity-50 pointer-events-none"}>
                <p className="text-[10px] uppercase tracking-wider font-bold text-white/40 mb-1">Your teams</p>
                <p className="text-[11px] text-white/35 mb-2">Turn on a whole league, or only the teams you follow. Once you pick any team, you only get those teams.</p>
                <div className="space-y-4">
                    <TeamGroup title="PWHL" lg="pwhl" teams={pwhlTeams} prefs={prefs} setKey={setKey} persist={persist} />
                    <TeamGroup title="NHL" lg="nhl" teams={nhlTeams.map((t) => ({ id: t.abbr, name: t.name, color: t.primary_color || "#8A94A6" }))} prefs={prefs} setKey={setKey} persist={persist} />
                </div>
            </div>
        </div>
    );
}

function TeamGroup({ title, lg, teams, prefs, setKey, persist }) {
    const chips = CHIPS.filter((c) => c[3] === "both" || c[3] === lg);
    // STRICT per-team (same as the server): a team alert is ON only when its own toggle is ON.
    const eff = (id, suf) => prefs[`team_${lg}_${id}_${suf}`] === true;
    const following = (id) => chips.some((c) => eff(id, c[0]));
    const toggleChip = (id, suf) => setKey(`team_${lg}_${id}_${suf}`, !eff(id, suf));
    const setAllTeam = (id, val) => { const n = { ...prefs }; chips.forEach((c) => { n[`team_${lg}_${id}_${c[0]}`] = val; }); persist(n); };

    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`notif-group-${lg}`}>
            <div className="space-y-1">
                {teams.map((t) => { const fol = following(t.id); return (
                    <div key={t.id} className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-2" data-testid={`notif-team-${lg}-${t.id}`}>
                        <div className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-2 min-w-0">
                                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: fol ? t.color : "transparent", border: fol ? "none" : "1px solid rgba(255,255,255,0.3)" }} />
                                <span className="text-xs font-bold text-white/85 truncate">{t.name}</span>
                            </span>
                            <button onClick={() => setAllTeam(t.id, !fol)} data-testid={`notif-team-follow-${lg}-${t.id}`} className="text-[10px] font-bold uppercase tracking-wider text-white/50 hover:text-[#B366F0] shrink-0">{fol ? "Unfollow" : "Follow"}</button>
                        </div>
                        {fol && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {chips.map(([suf, label, Icon]) => { const on = eff(t.id, suf); return (
                                    <button key={suf} onClick={() => toggleChip(t.id, suf)} data-testid={`notif-chip-${lg}-${t.id}-${suf}`}
                                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${on ? "bg-[#9D4CDD] text-white" : "bg-white/[0.06] text-white/40 hover:text-white/70"}`}>
                                        <Icon className="w-3 h-3" /> {label}
                                    </button>
                                ); })}
                            </div>
                        )}
                    </div>
                ); })}
                {teams.length === 0 && <span className="text-[11px] text-white/30">No teams available.</span>}
            </div>
        </div>
    );
}
